import React, { useRef } from "react";
import PropTypes from "prop-types";
import ChartRechartsBarCategorical from "./ChartRechartsBarCategorical";
import { useChartRenderer } from "./hooks/useChartRenderer";

const ChartCanvas = ({
  graphData,
  graphDataLength,
  graphTitle,
  chartType,
  barYAxisMetric,
  setBarYAxisMetric,
  hasComparison,
  dependentAnswerMapping,
  selectedIndepMeta,
  selectedDepMeta,
  isDataLoading,
  dataError,
  graphError,
  yearComparisonSeries,
  yearComparisonEnabled,
  containerId,
  sourceLine,
}) => {
  const containerRef = useRef(null);
  const tooltipRef = useRef(null);

  useChartRenderer({
    graphData,
    chartType,
    selectedIndepMeta,
    selectedDepMeta,
    dependentAnswerMapping,
    containerRef,
    tooltipRef,
  });

  const emptyStateMessage = isDataLoading
    ? "Loading study data..."
    : dataError || graphError || "Your chart will appear here once generated.";
  const emptyStateColor = dataError || graphError ? "text-red-600" : "text-gray-500";
  const shouldUseRechartsBarPath = ["grouped_bar", "lollipop"].includes(chartType);
  const depType = String(selectedDepMeta?.type || "").toLowerCase();
  const shouldShowYAxisMetricToggle =
    shouldUseRechartsBarPath && depType === "cat" && graphDataLength > 0;

  return (
    <section className="w-full bg-white shadow-md rounded-lg p-6">
      {graphDataLength ? (
        <div id={containerId} className="w-full" aria-busy={isDataLoading}>
          <h2 className="text-xl font-bold mb-4 text-center text-[#181010]">{graphTitle}</h2>
          {shouldShowYAxisMetricToggle && (
            <div className="mb-4 flex justify-end">
              <div className="inline-flex items-center rounded-lg border border-gray-300 bg-gray-50 p-1">
                <span className="px-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Y-axis
                </span>
                <button
                  type="button"
                  onClick={() => setBarYAxisMetric("frequency")}
                  className={`h-9 rounded-md px-3 text-sm font-semibold transition ${
                    barYAxisMetric === "frequency"
                      ? "bg-white text-[#181010] shadow-sm"
                      : "text-gray-600 hover:text-[#181010]"
                  }`}
                  aria-pressed={barYAxisMetric === "frequency"}
                >
                  Frequency (%)
                </button>
                <button
                  type="button"
                  onClick={() => setBarYAxisMetric("count")}
                  className={`h-9 rounded-md px-3 text-sm font-semibold transition ${
                    barYAxisMetric === "count"
                      ? "bg-white text-[#181010] shadow-sm"
                      : "text-gray-600 hover:text-[#181010]"
                  }`}
                  aria-pressed={barYAxisMetric === "count"}
                >
                  Total Count
                </button>
              </div>
            </div>
          )}
          {shouldUseRechartsBarPath ? (
            <ChartRechartsBarCategorical
              graphData={graphData}
              chartType={chartType}
              yAxisMetric={barYAxisMetric}
              hasComparison={hasComparison}
              dependentAnswerMapping={dependentAnswerMapping}
              selectedIndepMeta={selectedIndepMeta}
              selectedDepMeta={selectedDepMeta}
              yearComparisonSeries={yearComparisonSeries}
              yearComparisonEnabled={yearComparisonEnabled}
            />
          ) : (
            <div ref={containerRef} className="w-full" style={{ minHeight: 320 }} />
          )}
          {sourceLine && (
            <p className="mt-4 text-xs text-gray-500 border-t border-gray-100 pt-3">{sourceLine}</p>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-center h-full" aria-live="polite">
          <p className={`${emptyStateColor} text-center`}>{emptyStateMessage}</p>
        </div>
      )}
      <div
        ref={tooltipRef}
        role="tooltip"
        aria-hidden="true"
        className="pointer-events-none fixed z-[80] max-w-xs rounded-md bg-[#181010] px-2 py-1 text-xs text-white shadow-lg opacity-0 transition-opacity duration-100"
      />
    </section>
  );
};

ChartCanvas.propTypes = {
  graphData: PropTypes.arrayOf(PropTypes.object).isRequired,
  graphDataLength: PropTypes.number.isRequired,
  graphTitle: PropTypes.string.isRequired,
  chartType: PropTypes.string.isRequired,
  barYAxisMetric: PropTypes.oneOf(["frequency", "count"]).isRequired,
  setBarYAxisMetric: PropTypes.func.isRequired,
  hasComparison: PropTypes.bool.isRequired,
  dependentAnswerMapping: PropTypes.object,
  selectedIndepMeta: PropTypes.shape({
    question: PropTypes.string,
    label: PropTypes.string,
  }),
  selectedDepMeta: PropTypes.shape({
    question: PropTypes.string,
    label: PropTypes.string,
  }),
  isDataLoading: PropTypes.bool.isRequired,
  dataError: PropTypes.string,
  graphError: PropTypes.string,
  yearComparisonSeries: PropTypes.arrayOf(
    PropTypes.shape({
      yearLabel: PropTypes.string.isRequired,
      graphData: PropTypes.arrayOf(PropTypes.object).isRequired,
    })
  ),
  yearComparisonEnabled: PropTypes.bool,
  containerId: PropTypes.string,
  sourceLine: PropTypes.string,
};

ChartCanvas.defaultProps = {
  yearComparisonSeries: [],
  yearComparisonEnabled: false,
  containerId: "graph-container",
  sourceLine: "",
};

export default React.memo(ChartCanvas);
